import { ColumnState } from 'ag-grid-community'

import { ControlStatus } from '@/gen/controls/v1/control_status_pb'

import { controlStatusLabel } from '@/const/label'

import type { FilterModel } from '@/components/lemmala-design-system/table/types'

export interface ViewDefinition {
  label: string
  icon?: React.ReactNode
  // AG Grid filter model to apply for the view
  filterModel?: FilterModel
  // Column state drives visibility and widths per view
  columnState: ColumnState[]
  // IDs of filters to render in the custom FilterBar, in the desired order
  filterBarFilters: string[]
}

const buildColumnState = (groupByScope: boolean, hideWeight: boolean): ColumnState[] => [
  { colId: 'title', hide: false },
  { colId: 'scopeColumn', hide: groupByScope },
  { colId: 'controlWeight', hide: groupByScope || hideWeight },
  { colId: 'statusDisplay', hide: false },
  { colId: 'adherenceScope', hide: !groupByScope },
  { colId: 'smartQuestion', hide: true },
]

export type ViewName = 'All' | 'Gaps' | 'Validated' | 'No Evidence' | 'Not Applicable'

// Constants for view names
export const Views = {
  All: 'All',
  Gaps: 'Gaps',
  Validated: 'Validated',
  NoEvidence: 'No Evidence',
  NotApplicable: 'Not Applicable',
} as const satisfies Record<string, ViewName>

export const viewDefinitions: Record<ViewName, Omit<ViewDefinition, 'columnState'>> = {
  All: {
    label: 'All',
    icon: undefined,
    filterModel: {}, // No filter - show all controls
    filterBarFilters: ['scope', 'smartQuestion'],
  },
  Gaps: {
    label: 'Gaps',
    icon: undefined,
    filterModel: {
      status: {
        filterType: 'set',
        values: [controlStatusLabel[ControlStatus.GAP]],
      },
    },
    filterBarFilters: ['scope', 'smartQuestion'],
  },
  Validated: {
    label: 'Validated',
    icon: undefined,
    filterModel: {
      status: {
        filterType: 'set',
        values: [controlStatusLabel[ControlStatus.VALIDATED]],
      },
    },
    filterBarFilters: ['scope', 'smartQuestion'],
  },
  'No Evidence': {
    label: 'No evidence',
    icon: undefined,
    filterModel: {
      status: {
        filterType: 'set',
        values: [controlStatusLabel[ControlStatus.NO_EVIDENCE]],
      },
    },
    filterBarFilters: ['scope', 'smartQuestion'],
  },
  'Not Applicable': {
    label: 'Not applicable',
    icon: undefined,
    filterModel: {
      status: {
        filterType: 'set',
        values: [controlStatusLabel[ControlStatus.NOT_APPLICABLE]],
      },
    },
    filterBarFilters: ['scope', 'smartQuestion'],
  },
}

export const getViewDefinition = (
  name: string,
  groupByScope = false,
  hideWeight = false,
): ViewDefinition => {
  const view = viewDefinitions[name as ViewName] ?? viewDefinitions[Views.All]
  return { ...view, columnState: buildColumnState(groupByScope, hideWeight) }
}

// Type-safe helper to get view options - derived from viewDefinitions
export const viewOptions = Object.keys(viewDefinitions) as ViewName[]

// Type guard to check if a string is a valid view name
export function isValidViewName(name: string): name is ViewName {
  return viewOptions.includes(name as ViewName)
}

export const isColumnHiddenInView = (colId: string, viewName: string): boolean => {
  const view = getViewDefinition(viewName)
  const state = view.columnState.find((s) => s.colId === colId)
  if (state && typeof state.hide === 'boolean') return !!state.hide
  // Default to hidden if column is not described by the view
  return true
}
